import React from "react";
import Icon from "../components/Icon";
import ContactForm from "../components/ContactForm";
import {
  EMAIL,
  PHONE,
  WHATSAPP_URL,
} from "../constants/site";

export default function ContactPage() {
  return (
    <div className="relative">
      <div className="absolute inset-0 celestial-pattern pointer-events-none"></div>

      <section className="relative py-20 md:py-32 overflow-hidden page-x">
        <div className="max-w-max-width mx-auto text-center">
          <h1 className="text-display-responsive text-primary mb-6">
            Contact Online Quran Universe
          </h1>
          <p className="text-body-lg font-body-lg text-on-surface-variant max-w-2xl mx-auto mb-10">
            Connect with our global celestial education network. Whether you
            have questions about Tajweed or need a guide for your Hifz journey,
            we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="#contact-form"
              className="bg-primary text-on-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center text-label-md font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <Icon
                name="event_available"
                className="material-symbols-outlined"
              />
              Book a Free Trial
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-surface text-primary border-2 border-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center text-label-md font-bold hover:bg-primary-fixed transition-all flex items-center justify-center gap-2"
            >
              <Icon name="chat" className="material-symbols-outlined" />
              Speak to an Advisor
            </a>
          </div>
        </div>

        <div className="absolute top-20 right-10 opacity-20 hidden lg:block floating-element">
          <Icon
            name="auto_awesome"
            className="material-symbols-outlined text-[120px] text-primary"
          />
        </div>
      </section>

      <section className="pb-16 md:pb-24 page-x">
        <div className="max-w-max-width mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-gutter">
          <div className="bg-surface-container-lowest border border-outline-variant card-pad rounded-2xl shadow-sm hover:shadow-md transition-all group">
            <div className="w-12 h-12 bg-primary-fixed rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Icon
                name="whatshot"
                className="material-symbols-outlined text-primary"
              />
            </div>
            <h3 className="font-headline-md text-headline-md text-primary mb-2">
              WhatsApp
            </h3>
            <p className="text-on-surface-variant font-body-md mb-4">
              Chat directly with our support team.
            </p>
            <a
              className="text-primary font-bold hover:underline flex items-center gap-1"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {PHONE}
              <Icon
                name="arrow_forward"
                className="material-symbols-outlined text-sm"
              />
            </a>
          </div>

          <div className="bg-primary text-on-primary card-pad rounded-2xl shadow-lg md:col-span-1 relative overflow-hidden group">
            <div className="relative z-10">
              <div className="w-12 h-12 bg-on-primary/20 rounded-full flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform">
                <Icon
                  name="support_agent"
                  className="material-symbols-outlined text-on-primary"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-primary mb-2">
                24/7 Status
              </h3>
              <p className="text-on-primary/80 font-body-md">
                Our digital doors never close. Teachers available globally.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-label-md font-bold">Online Now</span>
              </div>
            </div>
            <div className="absolute -bottom-10 -right-10 opacity-10">
              <Icon
                name="schedule"
                className="material-symbols-outlined text-[150px]"
              />
            </div>
          </div>

          <div className="bg-surface-container-lowest border border-outline-variant card-pad rounded-2xl shadow-sm hover:shadow-md transition-all group">
            <div className="w-12 h-12 bg-secondary-container rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Icon
                name="mail"
                className="material-symbols-outlined text-secondary"
              />
            </div>
            <h3 className="font-headline-md text-headline-md text-primary mb-2">
              Email
            </h3>
            <p className="text-on-surface-variant font-body-md mb-4">
              Contact us for business inquiries.
            </p>
            <a
              className="text-primary font-bold hover:underline flex items-center gap-1"
              href={`mailto:${EMAIL}`}
            >
              {EMAIL}
            </a>
          </div>
        </div>
      </section>

      <section
        id="contact-form"
        className="section-y bg-surface-container-low page-x"
      >
        <div className="max-w-max-width mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-start">
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-primary-fixed/30">
            <div className="mb-10">
              <h2 className="text-headline-responsive text-primary mb-2">
                Send an Inquiry
              </h2>
              <p className="text-on-surface-variant font-body-md">
                Fill out the form below and our academic advisor will contact
                you within 24 hours.
              </p>
            </div>
            <ContactForm />
          </div>

          <div className="flex flex-col gap-12 lg:sticky lg:top-32">
            <div className="bg-tertiary-container/10 p-8 rounded-3xl border border-tertiary-fixed/30 relative">
              <div className="absolute -top-6 -right-6">
                <Icon
                  name="star_rate"
                  className="material-symbols-outlined text-6xl text-tertiary/20"
                />
              </div>
              <h4 className="font-headline-md text-tertiary font-bold mb-4 flex items-center gap-2">
                <Icon
                  name="auto_stories"
                  className="material-symbols-outlined"
                />
                Why Choose Us?
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Icon
                    name="check_circle"
                    className="material-symbols-outlined text-primary"
                  />
                  <span className="text-on-surface font-body-md">
                    Certified Teachers with Ijazah from world-renowned
                    institutes.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon
                    name="check_circle"
                    className="material-symbols-outlined text-primary"
                  />
                  <span className="text-on-surface font-body-md">
                    Flexible scheduling for students in the UK, USA, and beyond.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon
                    name="check_circle"
                    className="material-symbols-outlined text-primary"
                  />
                  <span className="text-on-surface font-body-md">
                    1-on-1 personalized attention for every student.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y page-x text-center">
        <div className="max-w-3xl mx-auto bg-primary rounded-section p-12 md:p-20 relative overflow-hidden shadow-2xl">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="grid grid-cols-6 h-full gap-2">
              <div className="border-r border-white/20"></div>
              <div className="border-r border-white/20"></div>
              <div className="border-r border-white/20"></div>
              <div className="border-r border-white/20"></div>
              <div className="border-r border-white/20"></div>
            </div>
          </div>
          <div className="relative z-10">
            <h2 className="font-display-lg text-headline-lg-mobile md:text-display-lg text-white mb-6">
              Book Your Free Trial Class Today
            </h2>
            <p className="text-white/80 font-body-lg mb-10">
              Experience the difference with a 30-minute complimentary
              assessment session with our expert tutors.
            </p>
            <div className="flex justify-center items-center">
              <a
                href="#contact-form"
                className="w-full md:w-auto bg-tertiary-fixed text-on-tertiary-fixed font-bold py-5 px-10 rounded-full flex items-center justify-center gap-3 hover:shadow-[0_0_20px_rgba(255,224,136,0.4)] transition-all cursor-pointer"
              >
                <Icon
                  name="phone_in_talk"
                  className="material-symbols-outlined"
                />
                Request a Call Back
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
